import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { EmailTemplateBlock } from '@marketing-hub/shared';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import styles from './EmailTemplateEditor.module.css';

type Props = {
  projectId: string;
  subject: string;
  blocks: EmailTemplateBlock[];
  bodyText: string;
  bodyHtml?: string;
  renderMode?: 'blocks' | 'html' | 'text';
  leadId?: string;
  /** Inbox-style chrome (bibliotheek) vs compact embed */
  presentation?: 'default' | 'inbox';
  /** Desktop 640px letter vs mobile 375px (inbox only). */
  viewport?: 'desktop' | 'mobile';
  /** Fallback als API geen envelope levert (legacy). */
  sampleRecipientLabel?: string;
  sampleSenderLabel?: string;
};

export function EmailTemplatePreviewPane({
  projectId,
  subject,
  blocks,
  bodyText,
  bodyHtml,
  renderMode = 'blocks',
  leadId,
  presentation = 'default',
  viewport = 'desktop',
  sampleRecipientLabel = 'Erik · erik@voorbeeld.nl',
  sampleSenderLabel = 'Jouw merk · hello@voorbeeld.nl',
}: Props) {
  const hasBody =
    renderMode === 'html'
      ? Boolean(bodyHtml?.trim())
      : renderMode === 'blocks'
        ? blocks.length > 0
        : Boolean(bodyText.trim());

  const previewQ = useQuery({
    queryKey: [
      'email-template-preview',
      projectId,
      subject,
      blocks,
      bodyText,
      bodyHtml,
      renderMode,
      leadId,
    ],
    enabled: Boolean(projectId) && subject.trim().length > 0 && hasBody,
    queryFn: () =>
      apiFetch<{
        preview: {
          subject: string;
          body_html: string | null;
          body_text: string;
          envelope?: {
            from_label: string;
            to_label: string;
            reply_to_label: string | null;
          };
        };
      }>('/api/email-templates/preview', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          lead_id: leadId,
          subject_template: subject,
          body_text_template: bodyText,
          body_html: bodyHtml,
          body_blocks: renderMode === 'blocks' ? blocks : undefined,
          render_mode: renderMode,
        }),
      }),
  });

  if (previewQ.isLoading) {
    return (
      <div className={presentation === 'inbox' ? styles.mailPreviewShell : undefined}>
        <LoadingState />
      </div>
    );
  }
  if (previewQ.isError) {
    return <p className={styles.muted}>Preview niet beschikbaar.</p>;
  }

  const resolvedSubject = previewQ.data?.preview.subject ?? subject;
  const envelope = previewQ.data?.preview.envelope;
  const fromLabel = envelope?.from_label ?? sampleSenderLabel;
  const toLabel = envelope?.to_label ?? sampleRecipientLabel;
  const replyLabel = envelope?.reply_to_label;
  const html = previewQ.data?.preview.body_html;
  const bodyContent = html ? (
    <iframe
      title="E-mail preview"
      className={styles.previewFrame}
      sandbox=""
      srcDoc={html}
    />
  ) : (
    <pre className={styles.plainPreview}>{previewQ.data?.preview.body_text ?? ''}</pre>
  );

  if (presentation !== 'inbox') {
    return (
      <div className={styles.previewWrap}>
        <p className={styles.muted}>
          <strong>Onderwerp:</strong> {resolvedSubject}
        </p>
        {bodyContent}
      </div>
    );
  }

  return (
    <div className={styles.mailPreviewShell} aria-label="E-mail voorbeeld">
      <div className={styles.mailPreviewInboxBar}>
        <div className={styles.mailPreviewMetaRow}>
          <span className={styles.mailPreviewMetaLabel}>Van</span>
          <span className={styles.mailPreviewMetaValue}>{fromLabel}</span>
        </div>
        {replyLabel ? (
          <div className={styles.mailPreviewMetaRow}>
            <span className={styles.mailPreviewMetaLabel}>Reply</span>
            <span className={styles.mailPreviewMetaValue}>{replyLabel}</span>
          </div>
        ) : null}
        <div className={styles.mailPreviewMetaRow}>
          <span className={styles.mailPreviewMetaLabel}>Aan</span>
          <span className={styles.mailPreviewMetaValue}>{toLabel}</span>
        </div>
        <p className={styles.mailPreviewSubject}>{resolvedSubject}</p>
      </div>
      <div
        className={[
          styles.mailPreviewViewport,
          viewport === 'mobile' ? styles.mailPreviewViewportMobile : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div
          className={[
            styles.mailPreviewLetter,
            viewport === 'mobile' ? styles.mailPreviewLetterMobile : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {bodyContent}
        </div>
      </div>
    </div>
  );
}
